/* eslint-disable max-len */
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { TargetPage } from '@1po/1po-bff-fe-spec/generated/backoffice/information/model/InformationBanner';
import { VehicleDetail } from '@1po/1po-bff-fe-spec/generated/common/vehicle/VehicleDetail';
import { ROUTER_CATALOG_TIRES } from 'app/AppRouter';
import { RootState } from 'app/AppStore';
import { ApplicationUrlParam, TechnicalDataIdParam } from 'domains/catalog/Catalog.types';
import { getBannersCount } from 'domains/information/Information.store';
import { Flex, LinkTabProps, LinkTabs, StickyBoxCustom } from 'UI';
import { getCatalogStickyTopOffset, shouldSuppressIamCatalogTopSpacing, useOffsetTop } from 'utils/hooks/useOffsetTop';
import { useCatalogTabItems } from './catalogTabItems';
import { CatalogConnectedTabBar, CatalogStickyHeaderOuter, CatalogStickyHeaderStack } from './CatalogTabs.styled';
import {
  CATALOG_HEADER_COLLAPSED_HEIGHT,
  CATALOG_HEADER_EXPANDED_EXTRA,
  CatalogVehicleHeader,
} from './CatalogVehicleHeader';
/* eslint-enable max-len */

export const CatalogTabs = ({
  result,
  vehicleImage,
  query,
  setCatalogShowMenu,
  setIsChangeVehicle,
  vehicleRouteChildren,
  tiresRouteChildren,
  catalogPage = 'RENAULT_CATALOG',
}: {
  result: VehicleDetail;
  vehicleImage: string | undefined;
  query: string;
  setCatalogShowMenu: (v: boolean) => void;
  setIsChangeVehicle?: (b: boolean) => void;
  vehicleRouteChildren?: React.ReactNode;
  tiresRouteChildren?: React.ReactNode;
  catalogPage?: TargetPage;
}) => {
  const location = useLocation();
  const offsetTop = useOffsetTop(catalogPage);
  const hasAdvertisementBanner = useSelector((state: RootState) => getBannersCount(state, catalogPage)) > 0;
  const catalogStickyOffset = getCatalogStickyTopOffset(offsetTop, catalogPage, hasAdvertisementBanner);
  const suppressTopSpacing = shouldSuppressIamCatalogTopSpacing(catalogPage, hasAdvertisementBanner);
  const [isExpanded, setIsExpanded] = useState(true);
  const isTiresRoute = location.pathname.includes(ROUTER_CATALOG_TIRES);

  useEffect(() => {
    const COLLAPSE_THRESHOLD = CATALOG_HEADER_COLLAPSED_HEIGHT + CATALOG_HEADER_EXPANDED_EXTRA;

    const handleScroll = () => {
      const scrollY = globalThis.scrollY;
      if (scrollY > COLLAPSE_THRESHOLD) {
        setIsExpanded(false);
      }
      if (scrollY === 0) {
        setIsExpanded(true);
      }
    };

    globalThis.addEventListener('scroll', handleScroll, { passive: true });
    return () => globalThis.removeEventListener('scroll', handleScroll);
  }, []);

  const items = useCatalogTabItems({
    result,
    vehicleRouteChildren,
    setCatalogShowMenu,
  });

  const linkTabsProps: LinkTabProps = {
    sticky: false,
    variant: 'blackPill',
    urlParam: TechnicalDataIdParam as ApplicationUrlParam,
    absolute: true,
    gap: 8,
    scrollable: true,
    items,
  };

  return (
    <Flex direction={'column'} data-cy={'catalog-tabs'}>
      <StickyBoxCustom offsetTop={catalogStickyOffset} zIndex={799}>
        <CatalogStickyHeaderOuter data-cy={'catalog-sticky-header-outer'} $suppressTopSpacing={suppressTopSpacing}>
          <CatalogStickyHeaderStack data-cy={'catalog-sticky-header-stack'}>
            <CatalogVehicleHeader
              result={result}
              vehicleImage={vehicleImage}
              query={query}
              isExpanded={isExpanded}
              onExpandedChange={setIsExpanded}
              setIsChangeVehicle={setIsChangeVehicle}
              connected
            />
            <CatalogConnectedTabBar>
              <LinkTabs {...linkTabsProps} section={'tabBar'} />
            </CatalogConnectedTabBar>
          </CatalogStickyHeaderStack>
        </CatalogStickyHeaderOuter>
      </StickyBoxCustom>
      {isTiresRoute && tiresRouteChildren}
      <LinkTabs {...linkTabsProps} scrollable={false} section={'routes'} />
    </Flex>
  );
};
